import React, { useState, useEffect } from 'react';
import {
  getApplianceTariff,
  getApplianceEnergyDaily,
} from '../../../../services';
import { DeltaPill } from '../../../../components';
import * as s from '../plant-availability/styled-plant-availability';

// Financeiro da usina: tarifa efetiva (override da usina ?? default do
// cliente) x energia no periodo = R$. Para UCHA e RECEITA (gera e entrega),
// para EBA e CUSTO (consome). Mostra '—' enquanto nao houver tarifa
// cadastrada (PATCH restrito a role privilegiado no backend).

const DAYS_BY_PERIOD = { day: 1, week: 7, month: 30 };

const PERIODS = [
  { key: 'day', label: 'Dia' },
  { key: 'week', label: 'Semana' },
  { key: 'month', label: 'Mês' },
];

const fmtBRL = (v) => {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  return Number(v).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const fmtKwh = (v) => {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  return Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 0 });
};

const PlantFinance = ({ applianceId, accent, refreshKey }) => {
  const [period, setPeriod] = useState('week');
  const [tariff, setTariff] = useState(null);
  const [energyKwh, setEnergyKwh] = useState(null);
  // Energia do periodo ANTERIOR (pra DeltaPill) — derivada no front pedindo
  // o dobro de dias e dividindo a serie ao meio. Evita campo novo no backend.
  const [previousEnergyKwh, setPreviousEnergyKwh] = useState(null);
  const [loading, setLoading] = useState(true);
  const isUcha = accent === 'ucha';

  // Tarifa efetiva (so muda por applianceId, nao por periodo).
  useEffect(() => {
    if (applianceId == null) return undefined;
    let active = true;
    getApplianceTariff(applianceId)
      .then((res) => {
        if (!active) return;
        const d = (res && res.data) || res || {};
        setTariff(
          d.effectiveTariff != null ? Number(d.effectiveTariff) : null,
        );
      })
      .catch(() => {
        if (active) setTariff(null);
      });
    return () => {
      active = false;
    };
  }, [applianceId, refreshKey]);

  // Energia no periodo.
  useEffect(() => {
    if (applianceId == null) return undefined;
    let active = true;
    setLoading(true);
    const days = DAYS_BY_PERIOD[period] || 7;
    // Pede 2x a janela: 1a metade = periodo anterior, 2a metade = atual.
    getApplianceEnergyDaily(applianceId, days * 2)
      .then((res) => {
        if (!active) return;
        const energyData =
          (res && ((res.result && res.result.data) || res.data)) || [];
        const series = Array.isArray(energyData) ? energyData : [];
        // Ordena por data asc e divide: ultimos `days` = atual, anteriores
        // = periodo passado. Split por contagem (serie diaria e densa).
        const sorted = series
          .slice()
          .sort((a, b) => String(a.date).localeCompare(String(b.date)));
        const sum = (arr) =>
          arr.reduce((acc, dd) => acc + Math.abs(Number(dd.energy) || 0), 0);
        const curr = sorted.slice(-days);
        const prev = sorted.slice(0, Math.max(0, sorted.length - days));
        setEnergyKwh(sum(curr));
        setPreviousEnergyKwh(prev.length ? sum(prev) : null);
        setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setEnergyKwh(null);
        setPreviousEnergyKwh(null);
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [applianceId, period]);

  const money =
    tariff != null && energyKwh != null ? tariff * energyKwh : null;
  const previousMoney =
    tariff != null && previousEnergyKwh != null
      ? tariff * previousEnergyKwh
      : null;
  const moneyLabel = isUcha ? 'Receita estimada' : 'Custo estimado';
  // Para EBA (custo/consumo) subida e RUIM -> invertSemantic. UCHA (receita/
  // geracao) subida e boa.
  const invert = !isUcha;

  return (
    <s.Wrap>
      <s.PeriodToggle>
        {PERIODS.map((p) => (
          <s.PeriodBtn
            key={p.key}
            type="button"
            $active={period === p.key}
            $accent={accent}
            onClick={() => setPeriod(p.key)}
          >
            {p.label}
          </s.PeriodBtn>
        ))}
      </s.PeriodToggle>

      <s.Kpis>
        <s.Kpi>
          <s.KpiLabel
            title={
              isUcha
                ? 'Receita estimada = tarifa × energia gerada no período.'
                : 'Custo estimado = tarifa × energia consumida no período.'
            }
          >
            {moneyLabel}
          </s.KpiLabel>
          <s.KpiValue
            style={{ color: isUcha ? 'var(--cco-green)' : 'var(--cco-ink)' }}
          >
            {money == null ? '—' : <><small>R$ </small>{fmtBRL(money)}</>}
          </s.KpiValue>
          <DeltaPill
            small
            invertSemantic={invert}
            current={money != null ? Number(money) : null}
            previous={previousMoney != null ? Number(previousMoney) : null}
          />
        </s.Kpi>
        <s.Sep />
        <s.Kpi>
          <s.KpiLabel title="Energia total (gerada para UCHA, consumida para EBA) no período selecionado acima.">
            Energia no período
          </s.KpiLabel>
          <s.KpiValue>
            {loading ? '—' : fmtKwh(energyKwh)}
            {!loading && energyKwh != null && <small>kWh</small>}
          </s.KpiValue>
          <DeltaPill
            small
            invertSemantic={invert}
            current={energyKwh != null ? Number(energyKwh) : null}
            previous={previousEnergyKwh != null ? Number(previousEnergyKwh) : null}
          />
        </s.Kpi>
        <s.Sep />
        <s.Kpi>
          <s.KpiLabel>Tarifa</s.KpiLabel>
          <s.KpiValue title="Tarifa efetiva: override da usina ou padrão do cliente">
            {tariff == null ? '—' : <><small>R$ </small>{fmtBRL(tariff)}</>}
            {tariff != null && <small>/kWh</small>}
          </s.KpiValue>
        </s.Kpi>
      </s.Kpis>

      {tariff == null && (
        <s.Empty>
          Sem tarifa cadastrada — defina a tarifa do cliente ou da usina para
          ver o {isUcha ? 'faturamento' : 'custo'} estimado.
        </s.Empty>
      )}
    </s.Wrap>
  );
};

export default PlantFinance;
